import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faFileAlt,
  faTags,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./PDFMetadataAnalysis.module.css";

const PDFMetadataAnalysis = ({ data, rawText, analysisData }) => {
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const formatDate = (dateString) => {
    if (!dateString || dateString === "Unknown" || dateString === "") {
      return "N/A";
    }
    try {
      if (dateString.startsWith("D:")) {
        dateString = dateString.substring(2);
      }
      if (dateString.length >= 8) {
        const year = dateString.substring(0, 4);
        const monthNum = parseInt(dateString.substring(4, 6), 10);
        const day = dateString.substring(6, 8);
        const month = MONTHS[monthNum - 1] || monthNum;
        return `${day} ${month} ${year}`;
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  const formatMetaValue = (val) => {
    if (val == null || val === "" || val === "Unknown") return "N/A";
    return String(val).trim();
  };

  const getTechnicalMetadata = () => {
    const metadata = analysisData?.metadata || {};
    const fallbackTitle = analysisData?.filename || analysisData?.fileData?.fileName;

    return [
      { property: "Title", value: formatMetaValue(metadata.title || fallbackTitle) },
      { property: "Author", value: formatMetaValue(metadata.author) },
      { property: "Created", value: formatDate(metadata.createdDate || metadata.creation_date) },
      { property: "Modified", value: formatDate(metadata.modificationDate || metadata.modification_date || metadata.modDate) },
      { property: "Producer", value: formatMetaValue(metadata.producer || metadata.creator) },
      { property: "PDF Version", value: formatMetaValue(metadata.pdfVersion || metadata.pdf_version) },
      { property: "Page Size", value: formatMetaValue(metadata.pageSize || metadata.page_size) },
    ];
  };

  const getEmbeddedMetadata = () => {
    const metadata = analysisData?.metadata || {};
    const embedded = {
      keywords: metadata.keywords || "",
      subject: metadata.subject || "",
      creator: metadata.creator || "",
      producer: metadata.producer || "",
    };

    return embedded;
  };

  const technicalMetadata = getTechnicalMetadata();
  const embeddedMetadata = getEmbeddedMetadata();

  const hasEmbeddedData =
    embeddedMetadata.keywords ||
    embeddedMetadata.subject ||
    embeddedMetadata.creator ||
    embeddedMetadata.producer;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FontAwesomeIcon icon={faInfoCircle} />
        </div>
        <div>
          <h2 className={styles.title}>Metadata Analysis</h2>
          <p className={styles.subtitle}>Document properties & information</p>
        </div>
      </div>

      {/* Section A: Technical Metadata */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FontAwesomeIcon icon={faFileAlt} className={styles.sectionIcon} />
          Technical Metadata
        </h3>
        <div className={styles.metadataTable}>
          <table>
            <thead>
              <tr>
                <th>Property</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {technicalMetadata.map((item, idx) => (
                <tr key={idx}>
                  <td className={styles.propertyCell}>{item.property}</td>
                  <td className={styles.valueCell}>{item.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section B: Embedded Metadata */}
      {hasEmbeddedData && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <FontAwesomeIcon icon={faTags} className={styles.sectionIcon} />
            Embedded Metadata
          </h3>
          <div className={styles.embeddedMetadata}>
            {embeddedMetadata.keywords && (
              <div className={styles.embeddedItem}>
                <div className={styles.embeddedLabel}>Keywords</div>
                <div className={styles.embeddedValue}>
                  {embeddedMetadata.keywords}
                </div>
              </div>
            )}
            {embeddedMetadata.subject && (
              <div className={styles.embeddedItem}>
                <div className={styles.embeddedLabel}>Subject</div>
                <div className={styles.embeddedValue}>
                  {embeddedMetadata.subject}
                </div>
              </div>
            )}
            {embeddedMetadata.creator && (
              <div className={styles.embeddedItem}>
                <div className={styles.embeddedLabel}>Creator</div>
                <div className={styles.embeddedValue}>
                  {embeddedMetadata.creator}
                </div>
              </div>
            )}
            {embeddedMetadata.producer && (
              <div className={styles.embeddedItem}>
                <div className={styles.embeddedLabel}>Producer</div>
                <div className={styles.embeddedValue}>
                  {embeddedMetadata.producer}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!hasEmbeddedData && technicalMetadata.length === 0 && (
        <div className={styles.emptyState}>
          <p>No metadata available</p>
        </div>
      )}
    </div>
  );
};

export default PDFMetadataAnalysis;
